import React, { useEffect, useState } from 'react';
import {
  Flex,
  Text,
  Button,
  PopoverTrigger,
  Portal,
  PopoverContent,
  PopoverBody,
  Popover,
  Checkbox,
  Divider,
  Input,
  IconButton,
  useToast,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import { BsFilterLeft } from 'react-icons/bs';

function InterviewMainFilter({
  setInterviewStatusScheduledFilter,
  interviewStatusScheduledFilter,
  interviewStatusCompletedFilter,
  setInterviewStatusCompletedFilter,
  interviewStatusReportPendingFilter,
  setInterviewStatusReportPendingFilter,
  interviewStatusMissingVideoFilter,
  setInterviewStatusMissingVideoFilter,
  startDateFilter,
  setStartDateFilter,
  endDateFilter,
  setEndDateFilter,
  handleApplySearch,
  handleEraseAllFilters,
}) {
  const initRef = React.useRef();
  const toast = useToast();
  const [errorDate, setErrorDate] = useState(false);

  const eraseCalendarDates = () => {
    setStartDateFilter('');
    setEndDateFilter('');
    setErrorDate(false);
  };

  const dateGeneratorAndSetter = chosenMonth => {
    const date = new Date();
    let curatedMonth = date.getMonth();

    // Curate month if we want to filter current or last month
    switch (chosenMonth) {
      case 'currentMonth':
        curatedMonth -= 1;
        break;
      case 'lastMonth':
        curatedMonth -= 2;
        break;
      default:
        curatedMonth -= 1;
        break;
    }

    // Advance a month if today's date is equal or higher than 21
    if (date.getDate() >= 21) curatedMonth += 1;

    const startDate = new Date(date.getFullYear(), curatedMonth, 21).toISOString();
    const endDate = new Date(date.getFullYear(), curatedMonth + 1, 20).toISOString();

    setStartDateFilter(startDate.substring(0, startDate.indexOf('T')));
    setEndDateFilter(endDate.substring(0, startDate.indexOf('T')));
  };

  const setCurrentMonthDates = () => dateGeneratorAndSetter('currentMonth');
  const setLastMonthDates = () => dateGeneratorAndSetter('lastMonth');

  useEffect(() => {
    const dateFrom = new Date(startDateFilter).getTime();
    const dateTo = new Date(endDateFilter).getTime();

    if (dateFrom > dateTo) {
      toast({
        position: 'bottom-right',
        isClosable: true,
        title: 'Date Filter Error',
        description: `End date can't be a date before Start Date`,
        status: 'warning',
        duration: null,
        variant: 'left-accent',
      });
      setErrorDate(true);
    }
    if (dateFrom > Date.now()) {
      toast({
        position: 'bottom-right',
        isClosable: true,
        title: 'Date Filter Error',
        description: `Start Date can't be a date after today`,
        status: 'warning',
        duration: null,
        variant: 'left-accent',
      });
      setErrorDate(true);
    }
    if (!(dateFrom > dateTo || dateFrom > Date.now())) setErrorDate(false);
  }, [startDateFilter, endDateFilter]);

  return (
    <Popover closeOnBlur={false} placement='bottom-end' initialFocusRef={initRef}>
      {({ _isOpen, onClose }) => (
        <>
          <PopoverTrigger>
            <Button color='gray.500' rightIcon={<BsFilterLeft size={28} />}>
              ADD FILTER
            </Button>
          </PopoverTrigger>
          <Portal>
            <PopoverContent bg='white.0' w='595px'>
              <PopoverBody>
                <Flex w='548px' mx='auto' alignItems='center' marginTop='10px'>
                  <Text as='span' fontSize='18px' marginRight='18px' lineHeight='28px' fontWeight='700'>
                    Interview Status:
                  </Text>
                  <Checkbox
                    size='lg'
                    marginRight='16px'
                    isChecked={interviewStatusScheduledFilter}
                    onChange={e => setInterviewStatusScheduledFilter(e.target.checked)}>
                    <Text fontSize='14px'>Scheduled</Text>
                  </Checkbox>
                  <Checkbox
                    size='lg'
                    marginRight='16px'
                    isChecked={interviewStatusReportPendingFilter}
                    onChange={e => setInterviewStatusReportPendingFilter(e.target.checked)}>
                    <Text fontSize='14px'>Report Pending</Text>
                  </Checkbox>
                  <Checkbox
                    size='lg'
                    marginRight='16px'
                    isChecked={interviewStatusMissingVideoFilter}
                    onChange={e => setInterviewStatusMissingVideoFilter(e.target.checked)}>
                    <Text fontSize='14px'>Missing Video</Text>
                  </Checkbox>
                  <Checkbox
                    size='lg'
                    marginRight='16px'
                    isChecked={interviewStatusCompletedFilter}
                    onChange={e => setInterviewStatusCompletedFilter(e.target.checked)}>
                    <Text fontSize='14px'>Completed</Text>
                  </Checkbox>
                </Flex>

                <Divider w='548px' marginX='auto' marginTop='12px' />

                <Flex w='548px' mx='auto' alignItems='center' marginTop='10px'>
                  <Text as='span' fontSize='18px' w='160px' marginRight='16px' lineHeight='28px' fontWeight='700'>
                    Report Delivered Time:
                  </Text>
                  <Input
                    size='sm'
                    w='135px'
                    type='date'
                    mr='16px'
                    borderRadius='48px'
                    backgroundColor='gray.50'
                    value={startDateFilter}
                    onChange={e => setStartDateFilter(e.target.value)}
                  />
                  <Divider w='10px' />
                  <Input
                    size='sm'
                    w='135px'
                    type='date'
                    ml='16px'
                    borderRadius='48px'
                    backgroundColor='gray.50'
                    value={endDateFilter}
                    onChange={e => setEndDateFilter(e.target.value)}
                  />
                  <IconButton
                    aria-label='erase Dates'
                    icon={<DeleteIcon />}
                    ml='8px'
                    variant='ghost'
                    onClick={eraseCalendarDates}
                  />
                </Flex>

                <Divider w='548px' marginX='auto' marginTop='12px' />

                <Flex w='548px' mx='auto' alignItems='center' marginTop='10px'>
                  <Button backgroundColor='gray.50' marginRight='16px' borderWidth='1px' onClick={setCurrentMonthDates}>
                    <Text fontSize='14px' fontWeight='400'>
                      Current Accounting Month
                    </Text>
                  </Button>
                  <Button backgroundColor='gray.50' marginRight='16px' borderWidth='1px' onClick={setLastMonthDates}>
                    <Text fontSize='14px' fontWeight='400'>
                      Last Accounting Month
                    </Text>
                  </Button>
                </Flex>

                <Divider w='548px' marginX='auto' marginTop='12px' />

                <Flex
                  w='548px'
                  mx='auto'
                  alignItems='center'
                  marginTop='10px'
                  justifyContent='space-between'
                  marginBottom='8px'>
                  <Button variant='link' onClick={handleEraseAllFilters}>
                    <Text
                      as='span'
                      fontSize='18px'
                      marginRight='18px'
                      lineHeight='28px'
                      fontWeight='700'
                      color='gray.600'
                      textDecoration='underline'>
                      Reset All Filters
                    </Text>
                  </Button>

                  <Flex alignItems='center'>
                    <Button marginRight='16px' colorScheme='gray' onClick={onClose} ref={initRef}>
                      Cancel
                    </Button>

                    <Button
                      colorScheme={errorDate ? 'gray' : 'blue'}
                      onClick={
                        errorDate
                          ? console.log('')
                          : () => {
                              handleApplySearch();
                              onClose();
                            }
                      }
                      disabled={errorDate}>
                      Apply
                    </Button>
                  </Flex>
                </Flex>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </>
      )}
    </Popover>
  );
}

export default InterviewMainFilter;
